import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useReportDamage } from "@/hooks/use-equipment-service";

interface ReportDamageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Utstyret skaden meldes på. null = velg i lista (fra /utstyr/service). */
  item: { id: string; name: string } | null;
  /** Utstyr å velge mellom når item er null. */
  items?: { id: string; name: string }[];
}

/** «Meld skade» (VIB-2921): beskrivelse, bilde og Brukbar / Ikke brukbar. */
export function ReportDamageDialog({ open, onOpenChange, item, items = [] }: ReportDamageDialogProps) {
  const [itemId, setItemId] = useState("");
  const [description, setDescription] = useState("");
  const [usable, setUsable] = useState<"yes" | "no">("yes");
  const [image, setImage] = useState<File | null>(null);
  const report = useReportDamage();

  useEffect(() => {
    if (open) {
      setItemId(item?.id ?? "");
      setDescription("");
      setUsable("yes");
      setImage(null);
    }
  }, [open, item]);

  const canSave = !!itemId && description.trim().length > 0 && !report.isPending;

  const save = () =>
    report.mutate(
      { itemId, description, usable: usable === "yes", image },
      {
        onSuccess: () => {
          toast.success(usable === "yes" ? "Skaden er meldt" : "Skaden er meldt, og utstyret er satt på service");
          onOpenChange(false);
        },
        onError: (e) => toast.error(e.message),
      },
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Meld skade{item ? ` på ${item.name}` : ""}</DialogTitle>
          <DialogDescription>Utstyrsansvarlig får varsel i Revehiet med en gang.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!item && (
            <div className="space-y-1.5">
              <Label htmlFor="damage-item">Utstyr</Label>
              <select
                id="damage-item"
                value={itemId}
                onChange={(e) => setItemId(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Velg utstyr</option>
                {items.map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="damage-description">Hva er galt?</Label>
            <Textarea
              id="damage-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="F.eks. viewfinder svart, løs kontakt i strømkabel"
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="damage-image">Bilde (valgfritt)</Label>
            <Input id="damage-image" type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] ?? null)} />
          </div>
          <RadioGroup value={usable} onValueChange={(v) => setUsable(v as "yes" | "no")} aria-label="Kan utstyret brukes?">
            <div className="flex items-start gap-2">
              <RadioGroupItem value="yes" id="damage-usable" className="mt-0.5" />
              <Label htmlFor="damage-usable" className="font-normal">
                <span className="font-medium">Brukbar</span>
                <span className="block text-xs text-muted-foreground">Kan fortsatt bookes og brukes.</span>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="no" id="damage-unusable" className="mt-0.5" />
              <Label htmlFor="damage-unusable" className="font-normal">
                <span className="font-medium">Ikke brukbar</span>
                <span className="block text-xs text-muted-foreground">Settes «På service» og kan ikke bookes før saken er ferdig.</span>
              </Label>
            </div>
          </RadioGroup>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={save} disabled={!canSave}>{report.isPending ? "Melder …" : "Meld skade"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
